/** @jsxRuntime classic */
/** @jsx jsx */
// /** @jsxFrag Fragment */
// import { Fragment } from 'react';
import { Link } from '@bottlebooks/gatsby-theme-base/src';
import { Box } from '@mantine/core';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import useRegistrations from '../useRegistrations';
import FloorPlanAreaPopover from './_FloorPlan.AreaPopover';
import FloorPlanAreaTooltip from './_FloorPlan.AreaTooltip';
import FloorPlanHotspot from './_FloorPlan.Hotspot';
import FloorPlanMain from './_FloorPlan.Main';

export type FloorPlanData = {
  image: { publicId: undefined | null | string };
  width: number;
  height: number;
  allHotspots: {
    slug: string;
    leftPercentage: number;
    topPercentage: number;
    widthPercentage: number;
    heightPercentage: number;
    area:
      | undefined
      | null
      | {
          slug: string;
          registrationId: undefined | null | string;
          registration:
            | undefined
            | null
            | {
                profile:
                  | undefined
                  | null
                  | {
                      name: undefined | null | string;
                      mainImage:
                        | undefined
                        | null
                        | { publicId: undefined | null | string };
                    };
              };
        };
  }[];
};

export const fragment = graphql`
  fragment FloorPlan on Bottlebooks_FloorPlan {
    image {
      publicId
    }
    width
    height
    allHotspots {
      slug
      leftPercentage
      topPercentage
      widthPercentage
      heightPercentage
      area {
        slug
        registrationId
        registration {
          profile {
            name
            mainImage {
              publicId
            }
          }
        }
      }
    }
  }
`;

export default function FloorPlan({
  data,
  title,
  id,
  primaryColor,
  contrastColor,
  highlightedSlug,
}: {
  title: string;
  id: string;
  primaryColor: string | undefined;
  contrastColor: string | undefined;
  data: FloorPlanData | null | undefined;
  highlightedSlug?: string;
}) {
  const registrations = useRegistrations();
  if (!data) return null;
  const plan = data;
  if (!plan.image.publicId || !plan.width || !plan.height) return null;
  return (
    <Box pos="relative">
      <FloorPlanMain
        title={title}
        id={id}
        imagePublicId={plan.image.publicId}
        width={plan.width}
        height={plan.height}
        primaryColor={primaryColor}
        contrastColor={contrastColor}
        elementIdToZoom={
          highlightedSlug ? `hotspot-${highlightedSlug}` : undefined
        }
        shouldAutoZoom
        maw="100%"
        mah="80vh"
        mx="auto"
        my="sm"
      >
        {plan.allHotspots.map(({ area, ...hotspot }) =>
          area ? (
            <FloorPlanHotspot
              key={hotspot.slug}
              position={{
                left: `${hotspot.leftPercentage}%`,
                top: `${hotspot.topPercentage}%`,
                width: `${hotspot.widthPercentage}%`,
                height: `${hotspot.heightPercentage}%`,
              }}
              id={`hotspot-${hotspot.slug}`}
              isHighlighted={hotspot.slug === highlightedSlug}
              primaryColor={primaryColor}
              contrastColor={contrastColor}
              isFavorite={registrations.isFavorite(area.registrationId)}
              popover={
                <Link
                  to={registrations.getLink(area.registrationId)}
                  sx={{ color: 'inherit', ':hover': { color: 'inherit' } }}
                >
                  <FloorPlanAreaPopover
                    title={area.registration?.profile?.name || ''}
                    imagePublicId={
                      area.registration?.profile?.mainImage?.publicId
                    }
                    isFavorite={registrations.isFavorite(area.registrationId)}
                    toggleFavorite={() =>
                      registrations.toggleFavorite(area.registrationId)
                    }
                  />
                </Link>
              }
              tooltip={
                <FloorPlanAreaTooltip
                  isFavorite={registrations.isFavorite(area.registrationId)}
                >
                  {area.registration?.profile?.name}
                </FloorPlanAreaTooltip>
              }
            />
          ) : null
        )}
      </FloorPlanMain>
    </Box>
  );
}
